// Parse duration strings like "14:04:00", "0:56:00" into minutes
export function parseDurationToMinutes(str) {
  if (!str || str.trim() === '') return null;
  const parts = str.trim().split(':');
  if (parts.length === 3) {
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    const s = parseInt(parts[2], 10) || 0;
    return h * 60 + m + s / 60;
  }
  return null;
}

// Parse various date formats from sheets
export function parseDate(str) {
  if (!str) return null;
  const cleaned = str.replace('T05:00:00.000Z', '').trim();
  const d = new Date(cleaned);
  return isNaN(d.getTime()) ? null : d;
}

// ========== SPEND METRICS ==========
export function processSpendData(spend) {
  const totalSpend = spend.reduce((sum, r) => sum + (parseFloat(r.Spend) || 0), 0);
  const totalClicks = spend.reduce((sum, r) => sum + (parseInt(r.Clicks) || 0), 0);
  const totalImpressions = spend.reduce((sum, r) => sum + (parseInt(r.Impressions) || 0), 0);
  const avgCTR = totalClicks / (totalImpressions || 1) * 100;
  const avgCPM = totalSpend / (totalImpressions || 1) * 1000;

  const dailySpend = spend
    .map(r => ({
      date: r.Date ? r.Date.split('T')[0] : '',
      spend: parseFloat(r.Spend) || 0,
      clicks: parseInt(r.Clicks) || 0,
      impressions: parseInt(r.Impressions) || 0,
    }))
    .filter(r => r.date)
    .sort((a, b) => a.date.localeCompare(b.date));

  return { totalSpend, totalClicks, totalImpressions, avgCTR, avgCPM, dailySpend };
}

// ========== LEAD OUTCOME (FB CONVERSIONS) ==========
export function processLeadOutcome(leads) {
  // Error/Duplicate should already be filtered out before calling this
  const totalLeads = leads.length;
  const statuses = {};
  const caseTypes = {};

  const CASE_TYPE_MAP = { 'Auto Accident': 'MVA' };
  const mapCaseType = (t) => CASE_TYPE_MAP[t] || t;

  leads.forEach(r => {
    const status = r.Status || 'Unknown';
    const caseType = mapCaseType(r['Case Type'] || 'Unknown');
    statuses[status] = (statuses[status] || 0) + 1;
    caseTypes[caseType] = (caseTypes[caseType] || 0) + 1;
  });

  const signed = statuses['Treating'] || 0;
  const rejected = statuses['Rejected'] || 0;
  const intake = statuses['Intake'] || 0;

  const caseTypeBreakdown = Object.entries(caseTypes)
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count);

  const monthly = {};
  leads.forEach(r => {
    const dateStr = r['Lead Status Changed'] || '';
    const d = parseDate(dateStr);
    if (!d) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!monthly[key]) monthly[key] = { total: 0, signed: 0, rejected: 0 };
    monthly[key].total++;
    if (r.Status === 'Treating') monthly[key].signed++;
    if (r.Status === 'Rejected') monthly[key].rejected++;
  });

  return {
    totalLeads, signed, rejected, intake,
    caseTypeBreakdown,
    statuses,
    monthly: Object.entries(monthly)
      .map(([month, data]) => ({ month, ...data }))
      .sort((a, b) => a.month.localeCompare(b.month)),
  };
}

// ========== SPEED TO LEAD ==========
export function processSpeedToLead(data) {
  const leads = data.filter(r => r['Inbound Date']);
  const totalLeads = leads.length;

  // Contact rate — look for the contact effort column (has trailing spaces in some sheets)
  const getContactCol = (r) => {
    const key = Object.keys(r).find(k =>
      k.toLowerCase().includes('when intake made an effort') ||
      k.toLowerCase().includes('intake contact')
    );
    return key ? r[key] : '';
  };

  const contacted = leads.filter(r => {
    const val = getContactCol(r);
    return val && val.trim() !== '';
  });
  const contactRate = (contacted.length / (totalLeads || 1)) * 100;

  // Parse time to first contact
  const getTimeCol = (r) => {
    const key = Object.keys(r).find(k =>
      k.toLowerCase().includes('time to first contact') ||
      k.toLowerCase().includes('time to contact')
    );
    return key ? r[key] : '';
  };

  const timesRaw = leads
    .map(r => parseDurationToMinutes(getTimeCol(r)))
    .filter(t => t !== null);

  const avgTimeMinutes = timesRaw.length > 0
    ? timesRaw.reduce((a, b) => a + b, 0) / timesRaw.length
    : 0;

  // TOD = Time of Day (In Hours / Out of Hours)
  const getTOD = (r) => {
    const key = Object.keys(r).find(k => {
      const lk = k.toLowerCase().trim();
      return lk === 'tod' || lk === 'tod (time of day)' || lk.includes('time of day') || lk.includes('in hours') || lk.includes('out of hours');
    });
    // If no named column, check by position — column G (index 6) in the array
    if (key) return (r[key] || '').trim();
    // Fallback: check all values for "In Hours" or "Out of Hours"
    const vals = Object.values(r);
    for (const v of vals) {
      if (typeof v === 'string' && (v.trim() === 'In Hours' || v.trim() === 'Out of Hours')) return v.trim();
    }
    return '';
  };

  // TOW = Time of Week (Weekday / Weekend)
  const getTOW = (r) => {
    const key = Object.keys(r).find(k => {
      const lk = k.toLowerCase().trim();
      return lk === 'tow' || lk === 'tow (time of week)' || lk.includes('time of week');
    });
    if (key) return (r[key] || '').trim();
    // Fallback: check all values for "Weekday" or "Weekend"
    const vals = Object.values(r);
    for (const v of vals) {
      if (typeof v === 'string' && (v.trim() === 'Weekday' || v.trim() === 'Weekend')) return v.trim();
    }
    return '';
  };

  const inHours = leads.filter(r => getTOD(r) === 'In Hours');
  const outHours = leads.filter(r => getTOD(r) === 'Out of Hours');

  const inHoursWithTime = inHours
    .map(r => parseDurationToMinutes(getTimeCol(r)))
    .filter(t => t !== null);
  const outHoursWithTime = outHours
    .map(r => parseDurationToMinutes(getTimeCol(r)))
    .filter(t => t !== null);

  const avgInHours = inHoursWithTime.length > 0
    ? inHoursWithTime.reduce((a, b) => a + b, 0) / inHoursWithTime.length
    : 0;
  const avgOutHours = outHoursWithTime.length > 0
    ? outHoursWithTime.reduce((a, b) => a + b, 0) / outHoursWithTime.length
    : 0;

  // Weekday vs weekend
  const weekdayLeads = leads.filter(r => getTOW(r) === 'Weekday');
  const weekendLeads = leads.filter(r => getTOW(r) === 'Weekend');

  const weekdayTimes = weekdayLeads
    .map(r => parseDurationToMinutes(getTimeCol(r)))
    .filter(t => t !== null);
  const weekendTimes = weekendLeads
    .map(r => parseDurationToMinutes(getTimeCol(r)))
    .filter(t => t !== null);

  const avgWeekday = weekdayTimes.length > 0
    ? weekdayTimes.reduce((a, b) => a + b, 0) / weekdayTimes.length
    : 0;
  const avgWeekend = weekendTimes.length > 0
    ? weekendTimes.reduce((a, b) => a + b, 0) / weekendTimes.length
    : 0;

  // Case type breakdown
  const CASE_TYPE_MAP = { 'Auto Accident': 'MVA' };
  const mapCaseType = (t) => CASE_TYPE_MAP[t] || t;
  const caseTypes = {};
  leads.forEach(r => {
    const ct = mapCaseType(r['Case Type'] || 'Unknown');
    if (!caseTypes[ct]) caseTypes[ct] = { total: 0, contacted: 0, times: [] };
    caseTypes[ct].total++;
    const contactVal = getContactCol(r);
    if (contactVal && contactVal.trim()) caseTypes[ct].contacted++;
    const t = parseDurationToMinutes(getTimeCol(r));
    if (t !== null) caseTypes[ct].times.push(t);
  });

  const caseTypeBreakdown = Object.entries(caseTypes)
    .map(([type, d]) => ({
      type,
      total: d.total,
      contacted: d.contacted,
      avgTime: d.times.length > 0 ? d.times.reduce((a, b) => a + b, 0) / d.times.length : null,
    }))
    .sort((a, b) => b.total - a.total);

  return {
    totalLeads,
    contactRate,
    contacted: contacted.length,
    avgTimeMinutes,
    avgInHours,
    avgOutHours,
    avgWeekday,
    avgWeekend,
    inHoursCount: inHours.length,
    outHoursCount: outHours.length,
    caseTypeBreakdown,
  };
}

// ========== CALL RAIL ==========
export function processCallRail(calls) {
  const totalCalls = calls.length;

  // Answer rate - match exact CallRail values
  const answered = calls.filter(r => {
    const status = (r['Call Status'] || '').trim();
    return status === 'Answered Call';
  });
  const missed = calls.filter(r => {
    const status = (r['Call Status'] || '').trim();
    return status === 'Abandoned Call' || status === 'Missed Call' || status === 'Voicemail';
  });
  const answerRate = (answered.length / (totalCalls || 1)) * 100;

  // First time callers
  const firstTimers = calls.filter(r => {
    const ft = (r['First-Time Caller'] || '').toLowerCase().trim();
    return ft === 'yes' || ft === 'true' || ft === '1';
  });

  // Missed by hour
  const missedByHour = {};
  missed.forEach(r => {
    const startTime = r['Start Time'] || '';
    const d = parseDate(startTime);
    if (!d) return;
    const hour = d.getHours();
    missedByHour[hour] = (missedByHour[hour] || 0) + 1;
  });

  const missedByHourArray = Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    label: formatHour(i),
    missed: missedByHour[i] || 0,
  }));

  // After hours (before 8am or after 5pm)
  const afterHoursCalls = calls.filter(r => {
    const d = parseDate(r['Start Time'] || '');
    if (!d) return false;
    const h = d.getHours();
    return h < 8 || h >= 17;
  });

  // Source breakdown
  const sources = {};
  calls.forEach(r => {
    const src = r.Source || r.utm_source || 'Unknown';
    if (!sources[src]) sources[src] = { total: 0, answered: 0, missed: 0, durations: [] };
    sources[src].total++;
    const status = (r['Call Status'] || '').trim();
    if (status === 'Answered Call') sources[src].answered++;
    if (status === 'Abandoned Call' || status === 'Missed Call' || status === 'Voicemail') sources[src].missed++;
    const dur = parseInt(r['Duration (seconds)']) || 0;
    if (dur > 0) sources[src].durations.push(dur);
  });

  const sourceBreakdown = Object.entries(sources)
    .map(([source, d]) => ({
      source,
      total: d.total,
      answered: d.answered,
      missed: d.missed,
      avgDuration: d.durations.length > 0
        ? d.durations.reduce((a, b) => a + b, 0) / d.durations.length
        : 0,
    }))
    .sort((a, b) => b.total - a.total);

  // Duration stats
  const durations = calls
    .map(r => parseInt(r['Duration (seconds)']) || 0)
    .filter(d => d > 0);
  const avgDuration = durations.length > 0
    ? durations.reduce((a, b) => a + b, 0) / durations.length
    : 0;

  return {
    totalCalls,
    answered: answered.length,
    missed: missed.length,
    answerRate,
    firstTimers: firstTimers.length,
    missedByHour: missedByHourArray,
    afterHoursCalls: afterHoursCalls.length,
    afterHoursPct: (afterHoursCalls.length / (totalCalls || 1)) * 100,
    sourceBreakdown,
    avgDuration,
  };
}

function formatHour(h) {
  if (h === 0) return '12a';
  if (h < 12) return `${h}a`;
  if (h === 12) return '12p';
  return `${h - 12}p`;
}

// ========== COMBINED METRICS ==========
export function computeMoneyMetrics(spend, leadOutcome) {
  const spendData = processSpendData(spend);
  const leadData = processLeadOutcome(leadOutcome);

  const costPerLead = spendData.totalSpend / (leadData.totalLeads || 1);
  const costPerSigned = spendData.totalSpend / (leadData.signed || 1);

  return {
    ...spendData,
    totalLeads: leadData.totalLeads,
    signed: leadData.signed,
    rejected: leadData.rejected,
    costPerLead,
    costPerSigned,
    signRate: (leadData.signed / (leadData.totalLeads || 1)) * 100,
  };
}

// Format minutes into readable string
export function formatMinutes(mins) {
  if (mins === null || mins === undefined) return '—';
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${m}m`;
}